import { BoxProps, Flex, Text, Tooltip, IconButton } from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";
import { CloseIcon } from "@chakra-ui/icons";
import { Z_INDEX_MODAL } from "../../../screens/root/constants";

export interface TagBadgeProps extends BoxProps {
  icon?: React.ReactNode;
  text: string;
  background?: string;
  contentColor?: string;
  isLarge?: boolean;
  displayTooltip?: boolean;
  tooltipText?: string | JSX.Element;
}

export function TagBadge({
  icon,
  text,
  background,
  contentColor,
  isLarge,
  displayTooltip = false,
  tooltipText,
  ...props
}: TagBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  // Hide tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        badgeRef.current &&
        !badgeRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTooltip]);

  const badge = (
    <Flex
      ref={badgeRef}
      width="fit-content"
      gap="5px"
      alignItems="center"
      background={background ?? "charcoal.900"}
      borderRadius="4px"
      p="4px 8px"
      color={contentColor ?? "neutral.200"}
      onClick={
        displayTooltip
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTooltip((prev) => !prev);
            }
          : undefined
      }
      cursor={displayTooltip ? "pointer" : "default"}
      {...props}
    >
      {icon}
      <Text
        textTransform="uppercase"
        fontSize={isLarge ? "10px" : "8px"}
        fontWeight="700"
        lineHeight="150%"
        letterSpacing={isLarge ? ".25px" : undefined}
      >
        {text}
      </Text>
    </Flex>
  );

  if (!displayTooltip) return badge;

  return (
    <Tooltip
      id="tag-badge-tooltip"
      isOpen={showTooltip}
      borderRadius="8px"
      p="0px"
      zIndex={Z_INDEX_MODAL}
      label={
        <Flex
          alignItems="start"
          p="16px"
          pr="8px"
          maxW="228px"
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Text
            fontSize="14px"
            fontFamily="Bricolage Grotesque"
            fontWeight="500"
            lineHeight="140%"
            color="reader.neutral.50"
          >
            {tooltipText}
          </Text>
          <IconButton
            aria-label="Dismiss tooltip"
            icon={<CloseIcon boxSize="9px" />}
            variant="ghost"
            alignSelf="flex-start"
            mt="-6px"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowTooltip(false);
            }}
          />
        </Flex>
      }
      backgroundColor="transparent.white.10"
      placement="bottom-start"
      closeOnClick={false}
      backdropFilter="blur(24px)"
      sx={{ pointerEvents: "auto" }}
    >
      {badge}
    </Tooltip>
  );
}
